import {
	useState,
	useRef,
	useEffect,
	useCallback,
	useMemo,
	lazy,
	Suspense,
} from "react";
import {
	DndContext,
	DragOverlay,
	pointerWithin,
	rectIntersection,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type CollisionDetection,
	type DragStartEvent,
	type DragEndEvent,
} from "@dnd-kit/core";
import { Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
	createTask,
	updateTask,
	deleteTask,
	getTaskDetails,
	getTaskDetailsFromTaskID,
	isTaskConflict,
	type Task,
	type ApiTaskStatus,
	type CreateTaskPayload,
	type UpdateTaskPayload,
} from "@/services/task.service";
import { listProjects, type Project } from "@/services/project.service";
import {
	listSprints,
	type Sprint,
	type EndSprintResponse,
	type StartSprintResponse,
} from "@/services/sprint.service";
import { listProfiles, type Profile } from "@/services/profile.service";
import { PermissionGate } from "@/components/PermissionGate";
import { usePermission } from "@/hooks/usePermission";
import {
	type UiTask,
	type Columns,
	type ColumnId,
	toUiTask,
	buildColumns,
	columnIdToApiStatus,
	sortStages,
	placementToWorkflowStage,
	allAssignees,
} from "@/components/tasks/types";
import { SYSTEM_STAGES } from "@/components/tasks/system-stages";
import {
	listWorkflowStages,
	type WorkflowStage,
} from "@/services/workflow-stage.service";
import {
	getStagePlacement,
	type WorkflowStagePlacementEntry,
} from "@/services/workflow-stage-catalog.service";
import { TaskCardContent } from "@/components/tasks/TaskCard";
import { SwimlaneBoard } from "@/components/tasks/SwimlaneBoard";
import { ColumnBoard } from "@/components/tasks/ColumnBoard";
import { isCellId, parseCellStageKey } from "@/components/tasks/swimlane";
import { TaskTable } from "@/components/tasks/TaskTable";
import { TaskFilters } from "@/components/tasks/TaskFilters";
import { UNASSIGNED_USER_ID } from "@/components/tasks/UserMultiSelect";
import { NewTaskDrawerV2 } from "@/components/tasks/NewTaskDrawerV2";
import { EditTaskDialog } from "@/components/tasks/EditTaskDialog";
import { TaskDetailDrawerV2 } from "@/components/tasks/TaskDetailDrawerV2";
import {
	TaskConflictDialog,
	type MergedText,
} from "@/components/tasks/TaskConflictDialog";
import { EndSprintButton } from "@/components/tasks/end-sprint/EndSprintButton";
import { StartSprintButton } from "@/components/tasks/start-sprint/StartSprintButton";
import { TasksPageSkeleton } from "@/components/tasks/TasksPageSkeleton";
import { CalendarSkeleton } from "@/components/tasks/calendar/CalendarSkeleton";
import { useSearchParams } from "react-router-dom";
import { useSWRConfig } from "swr";
import { useApiSWR } from "@/hooks/useApiSWR";
import { useTaskFilters } from "@/hooks/useTaskFilters";
import { useTaskFeed } from "@/hooks/useTaskFeed";
import { LoadMoreTasks } from "@/components/tasks/LoadMoreTasks";
import { useGcalCallbackToast } from "@/hooks/useGcalCallbackToast";
import { useAuth } from "@/context/AuthContext";

// Kept out of the Tasks page chunk; fetched on the first switch to Calendar.
const CalendarView = lazy(
	() => import("@/components/tasks/calendar/CalendarView"),
);

const COLLAPSED_STAGES_KEY = "tasks:collapsedStages";

export default function TasksPage() {
	const { user } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();

	const taskId = searchParams.get("taskId");
	const projectId = searchParams.get("projectId");
	const assignee = searchParams.get("assignee");

	const {
		data: filterSprintOptions = [],
		isLoading: filterSprintsLoading,
		mutate: mutateSprints,
	} = useApiSWR<Sprint[]>(["sprints"], listSprints);
	const activeSprint = useMemo(
		() => filterSprintOptions.find((s) => s.status === "active") ?? null,
		[filterSprintOptions],
	);

	const {
		filterProject,
		setFilterProject,
		filterSprint,
		setFilterSprint,
		filterUserIds,
		setFilterUserIds,
		filterStatus,
		setFilterStatus,
		search,
		setSearch,
		view,
		setView,
		clearFilters,
	} = useTaskFilters(searchParams, setSearchParams, activeSprint?.id);

	// Project/sprint filter on the server; held until sprints load so the
	// default (active) sprint applies to the first fetch.
	const {
		tasks: rawTasks,
		total: totalTasks,
		error: tasksError,
		isLoading: tasksLoading,
		isValidating: tasksValidating,
		isLoadingMore,
		hasMore,
		loadMore,
		patchTasks,
		refresh: mutateTasks,
	} = useTaskFeed(
		filterSprintsLoading
			? null
			: {
					project_id: filterProject !== "all" ? filterProject : undefined,
					sprint_id: filterSprint !== "all" ? filterSprint : undefined,
				},
	);
	const { mutate: globalMutate } = useSWRConfig();
	const {
		data: projects = [],
		error: projectsError,
		isLoading: projectsLoading,
	} = useApiSWR<Project[]>(["projects"], () => listProjects());
	const {
		data: profiles = [],
		error: profilesError,
		isLoading: profilesLoading,
	} = useApiSWR<Profile[]>(["profiles"], () => listProfiles());

	const { data: projectStages } = useApiSWR<WorkflowStage[]>(
		filterProject !== "all" ? ["workflow-stages", filterProject] : null,
		() => listWorkflowStages(filterProject),
	);
	const { data: placement } = useApiSWR<WorkflowStagePlacementEntry[]>(
		filterProject === "all" ? ["workflow-stage-placement"] : null,
		() => getStagePlacement(),
	);

	const stages = useMemo<WorkflowStage[]>(() => {
		if (filterProject !== "all")
			return sortStages(projectStages ?? SYSTEM_STAGES);
		if (!placement) return SYSTEM_STAGES;
		return sortStages(placement.map(placementToWorkflowStage));
	}, [projectStages, placement, filterProject]);
	const stageKeys = useMemo(() => stages.map((s) => s.key), [stages]);

	// Board (swimlane) view excludes the cancelled stage.
	const boardStages = useMemo(
		() => stages.filter((s) => s.key !== "cancelled"),
		[stages],
	);
	const boardStageKeys = useMemo(
		() => boardStages.map((s) => s.key),
		[boardStages],
	);

	// Cache UiTask by raw Task identity. SWR keeps refs stable for untouched
	// tasks across optimistic mutations, so only the moved/edited task gets a
	// new UiTask ref. Keeps memo(DraggableTaskCard) effective during drag.
	const taskCacheRef = useRef(new WeakMap<Task, UiTask>());
	const uiTasks = useMemo<UiTask[]>(() => {
		const cache = taskCacheRef.current;
		return rawTasks.map((t) => {
			let u = cache.get(t);
			if (!u) {
				u = toUiTask(t);
				cache.set(t, u);
			}
			return u;
		});
	}, [rawTasks]);

	const columns = useMemo<Columns>(
		() => buildColumns(uiTasks, stages),
		[uiTasks, stages],
	);

	const projectsById = useMemo(
		() => new Map(projects.map((p) => [p.id, p])),
		[projects],
	);

	const error = tasksError ?? projectsError ?? profilesError ?? null;

	const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
	const [collapsedProjects, setCollapsedProjects] = useState<
		Record<string, boolean>
	>({});
	const toggleProject = useCallback(
		(projectId: string) =>
			setCollapsedProjects((prev) => ({
				...prev,
				[projectId]: !prev[projectId],
			})),
		[],
	);
	// Per-stage column collapse, persisted per browser.
	const [collapsedStages, setCollapsedStages] = useState<
		Record<string, boolean>
	>(() => {
		try {
			return JSON.parse(
				localStorage.getItem(COLLAPSED_STAGES_KEY) ?? "{}",
			);
		} catch {
			return {};
		}
	});
	const toggleStage = useCallback(
		(stageKey: string) =>
			setCollapsedStages((prev) => {
				const next = { ...prev };
				if (next[stageKey]) delete next[stageKey];
				else next[stageKey] = true;
				try {
					localStorage.setItem(
						COLLAPSED_STAGES_KEY,
						JSON.stringify(next),
					);
				} catch {
					// ignore storage failures
				}
				return next;
			}),
		[],
	);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editTask, setEditTask] = useState<UiTask | null>(null);
	const [viewTask, setViewTask] = useState<UiTask | null>(null);
	const [conflict, setConflict] = useState<{
		task: UiTask;
		payload: UpdateTaskPayload;
		server: Task;
	} | null>(null);
	const [childParent, setChildParent] = useState<UiTask | null>(null);
	const [newTaskPreset, setNewTaskPreset] = useState<{
		status: string;
		projectId?: string;
	} | null>(null);
	const openNewTask = useCallback((status: string, projectId?: string) => {
		setNewTaskPreset({ status, projectId });
		setDialogOpen(true);
	}, []);
	const { can } = usePermission();
	const onAddTask = can("Create & edit tasks") ? openNewTask : undefined;
	const [expanded, setExpanded] = useState(false);
	const [grouped, setGrouped] = useState(true);

	const boardRef = useRef<HTMLDivElement>(null);
	const [canLeft, setCanLeft] = useState(false);
	const [canRight, setCanRight] = useState(true);

	const updateScrollEdges = useCallback(() => {
		const el = boardRef.current;
		if (!el) return;
		setCanLeft(el.scrollLeft > 0);
		setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
	}, []);

	const scrollBoard = useCallback((dir: -1 | 1) => {
		const el = boardRef.current;
		if (!el) return;
		el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
	}, []);

	useEffect(() => {
		const el = boardRef.current;
		if (!el || view !== "board") return;
		const raf = requestAnimationFrame(updateScrollEdges);
		el.addEventListener("scroll", updateScrollEdges, { passive: true });
		window.addEventListener("resize", updateScrollEdges);
		return () => {
			cancelAnimationFrame(raf);
			el.removeEventListener("scroll", updateScrollEdges);
			window.removeEventListener("resize", updateScrollEdges);
		};
	}, [view, expanded, stages, columns, updateScrollEdges]);

	const loading =
		tasksLoading ||
		projectsLoading ||
		profilesLoading ||
		filterSprintsLoading;
	const columnsRef = useRef(columns);
	columnsRef.current = columns;

	//open task details when taskId and projectId is present on url search params
	useEffect(() => {
		const t = setTimeout(() => {
			if (taskId && typeof taskId === "string" && viewTask === null) {
				const fetchTask = projectId
					? getTaskDetails(projectId, taskId)
					: getTaskDetailsFromTaskID(taskId);
				fetchTask.then((data) => {
					setViewTask(toUiTask(data));
				});
			}

			if (assignee === "me" && user) {
				setFilterUserIds([user.id]);
			}
		}, 250);
		return () => clearTimeout(t);
	}, [taskId, projectId, viewTask, assignee]);

	useGcalCallbackToast("Open a task and click Add to Calendar again.");

	const removeParams = () => {
		const newParams = new URLSearchParams(searchParams);
		newParams.delete("taskId");
		newParams.delete("projectId");
		setSearchParams(newParams);
	};

	// ── DnD ──────────────────────────────────────────────────────────────────

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
		useSensor(KeyboardSensor),
	);

	function findColumnId(taskId: string): ColumnId | null {
		for (const colId of stageKeys) {
			if (columnsRef.current[colId]?.some((t) => t.id === taskId))
				return colId;
		}
		return null;
	}

	// Resolve the stage (column) an `over` id points at: a swimlane cell
	// droppable (`projectId::stageKey`) or a bare stage key. Cards are not
	// droppables.
	function resolveStageId(overId: string): ColumnId | null {
		const key = isCellId(overId) ? parseCellStageKey(overId) : overId;
		return key && stageKeys.includes(key) ? (key as ColumnId) : null;
	}

	const collisionDetection: CollisionDetection = useCallback((args) => {
		const pointerHits = pointerWithin(args);
		if (pointerHits.length > 0) return pointerHits;
		return rectIntersection(args);
	}, []);

	const onDragStart = useCallback(({ active }: DragStartEvent) => {
		setActiveTaskId(active.id as string);
	}, []);

	// The task moves once, on drop; while dragging, the target cell only
	// highlights (isOver). Moving it on every drag-over re-rendered the whole
	// page and shifted the grid under the pointer, which caused the lag.
	const onDragEnd = useCallback(
		({ active, over }: DragEndEvent) => {
			setActiveTaskId(null);
			if (!over) return;

			const taskId = active.id as string;
			const srcCol = findColumnId(taskId);
			const dstCol = resolveStageId(over.id as string);
			if (!srcCol || !dstCol || srcCol === dstCol) return;
			const task = columnsRef.current[srcCol]?.find((t) => t.id === taskId);
			if (!task) return;

			const status = columnIdToApiStatus(dstCol);
			const destName = stages.find((s) => s.key === dstCol)?.name ?? dstCol;
			patchTasks((curr) =>
				curr.map((t) => (t.id === taskId ? { ...t, status } : t)),
			);
			updateTask(task.project_id, taskId, { status })
				.then((apiTask) => {
					// Splice the saved row in rather than refetching every task, so
					// other cards keep their refs (and memo). Merge, not replace: the
					// PATCH row lacks list-only fields (estimates, checklist counts),
					// and its fresh updated_at avoids a false conflict on next edit.
					patchTasks((curr) =>
						curr.map((t) =>
							t.id === taskId ? { ...t, ...apiTask } : t,
						),
					);
					toast.success("Task updated", {
						description: `Moved to ${destName}`,
					});
				})
				.catch(() => {
					toast.error("Failed to move task", {
						description: "Please try again.",
					});
					mutateTasks();
				});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[mutateTasks, patchTasks, stageKeys, stages],
	);

	// ── Filtering ────────────────────────────────────────────────────────────

	const filteredColumns = useMemo<Columns>(() => {
		const lc = search.toLowerCase();
		return stageKeys.reduce((acc, colId) => {
			acc[colId] = (columns[colId] ?? []).filter((t) => {
				if (filterProject !== "all" && t.project_id !== filterProject)
					return false;
				if (
					filterSprint !== "all" &&
					(filterSprint === "none" ? !!t.sprint : t.sprint?.id !== filterSprint)
				)
					return false;
				if (filterUserIds.length > 0) {
					const assignees = allAssignees(t);
					const match =
						(filterUserIds.includes(UNASSIGNED_USER_ID) &&
							assignees.length === 0) ||
						assignees.some((p) => filterUserIds.includes(p.id));
					if (!match) return false;
				}
				if (
					filterStatus !== "all" &&
					t.apiStatus !== (filterStatus as ApiTaskStatus)
				)
					return false;
				if (search && !t.title.toLowerCase().includes(lc)) return false;
				return true;
			});
			return acc;
		}, {} as Columns);
	}, [
		columns,
		filterProject,
		filterSprint,
		filterUserIds,
		filterStatus,
		search,
		stageKeys,
	]);

	// Board reads `filteredColumns` per stage; list and calendar want it flat.
	const flatFilteredTasks = useMemo(
		() =>
			view === "board"
				? []
				: stageKeys.flatMap((c) => filteredColumns[c] ?? []),
		[view, filteredColumns, stageKeys],
	);

	const allTasks = useMemo(
		() => stageKeys.flatMap((c) => columns[c] ?? []),
		[columns, stageKeys],
	);

	const nextPlannedSprint = useMemo<Sprint | null>(() => {
		if (activeSprint) return null;
		// const today = new Date().toISOString().slice(0, 10);
		const planned = filterSprintOptions
			.filter((s) => s.status === "planned")
			.sort((a, b) => a.start_date.localeCompare(b.start_date));
		return planned[0] ?? null;
	}, [filterSprintOptions, activeSprint]);

	const activeTask = useMemo(
		() =>
			activeTaskId
				? (stageKeys
						.flatMap((c) => columns[c] ?? [])
						.find((t) => t.id === activeTaskId) ?? null)
				: null,
		[activeTaskId, columns, stageKeys],
	);

	// Clearing drops the sprint param, which falls back to the active sprint.
	const handleClearFilters = clearFilters;

	const isFiltered =
		filterProject !== "all" ||
		filterSprint !== "all" ||
		filterUserIds.length > 0 ||
		filterStatus !== "all" ||
		search !== "";

	// ── Handlers ─────────────────────────────────────────────────────────────

	const handleCreateTask = useCallback(
		async (projectId: string, payload: CreateTaskPayload) => {
			const apiTask = await createTask(projectId, payload);
			mutateTasks();
			if (payload.parent_task_id)
				globalMutate(["task-children", payload.parent_task_id]);
			toast.success("Task created", { description: apiTask.title });
		},
		[mutateTasks, globalMutate],
	);

	const handleDeleteTask = useCallback(
		async (task: UiTask) => {
			patchTasks((curr) => curr.filter((t) => t.id !== task.id));
			try {
				await deleteTask(task.project_id, task.id);
				toast.success("Task deleted", { description: task.title });
				mutateTasks();
			} catch {
				mutateTasks();
				toast.error("Failed to delete task", {
					description: "Please try again.",
				});
			}
		},
		[mutateTasks, patchTasks],
	);

	// Optimistic-concurrency save: injects the updated_at the user last saw so the
	// server can reject a stale write. On conflict, opens the resolution modal
	// instead of throwing, leaving the editing surface free to resolve it.
	const saveWithConflict = useCallback(
		async (
			task: UiTask,
			payload: UpdateTaskPayload,
			onSuccess: (apiTask: Task) => void,
		): Promise<void> => {
			const body: UpdateTaskPayload = {
				...payload,
				expected_updated_at:
					payload.expected_updated_at ?? task.updated_at,
			};
			try {
				const apiTask = await updateTask(
					task.project_id,
					task.id,
					body,
				);
				onSuccess(apiTask);
			} catch (e) {
				if (isTaskConflict(e)) {
					setConflict({ task, payload, server: e.data });
					return;
				}
				throw e;
			}
		},
		[],
	);

	const handleSaveNotes = useCallback(
		async (task: UiTask, notes: string) => {
			await saveWithConflict(
				task,
				{ developer_notes: notes },
				(apiTask) => {
					const updated = toUiTask(apiTask);
					mutateTasks();
					if (updated) setViewTask(updated);
					toast.success("Notes saved");
				},
			);
		},
		[saveWithConflict, mutateTasks],
	);

	const handleChangeStatus = useCallback(
		async (task: UiTask, status: ApiTaskStatus) => {
			await saveWithConflict(task, { status }, (apiTask) => {
				const updated = toUiTask(apiTask);
				mutateTasks();
				if (!updated) {
					setViewTask(null);
					toast.success("Status updated");
					return;
				}
				setViewTask(updated);
				toast.success("Status updated", {
					description:
						stages.find((s) => s.key === updated.columnId)?.name ??
						updated.columnId,
				});
			});
		},
		[saveWithConflict, mutateTasks, stages],
	);

	const handleSprintStarted = useCallback(
		async (result: StartSprintResponse) => {
			try {
				await mutateSprints();
				setFilterSprint(result.sprint.id);
				mutateTasks();
			} catch {
				toast.error("Sprint started, but failed to refresh board.", {
					description: "Reload the page to see latest state.",
				});
			}
		},
		[mutateSprints, setFilterSprint, mutateTasks],
	);

	const handleSprintEnded = useCallback(
		async (_result: EndSprintResponse) => {
			try {
				await mutateSprints();
				setFilterSprint("all");
				mutateTasks();
			} catch {
				toast.error("Sprint ended, but failed to refresh board.", {
					description: "Reload the page to see latest state.",
				});
			}
		},
		[mutateSprints, setFilterSprint, mutateTasks],
	);

	const handleEditTask = useCallback(
		async (task: UiTask, payload: UpdateTaskPayload) => {
			await saveWithConflict(task, payload, (apiTask) => {
				const updated = toUiTask(apiTask);
				mutateTasks();
				if (updated) {
					setViewTask((vt) =>
						vt && vt.id === updated.id ? updated : vt,
					);
					setEditTask((et) =>
						et && et.id === updated.id ? updated : et,
					);
				}
				toast.success("Task updated", { description: apiTask.title });
			});
		},
		[saveWithConflict, mutateTasks],
	);

	const resolveConflict = useCallback(
		async (merge: boolean, text?: MergedText) => {
			if (!conflict) return;
			const { task, payload, server } = conflict;
			const body: UpdateTaskPayload = {
				...payload,
				expected_updated_at: server.updated_at,
			};
			if (merge && text) {
				if (text.title !== undefined) body.title = text.title;
				if (text.description !== undefined)
					body.description = text.description;
				if (text.developer_notes !== undefined)
					body.developer_notes = text.developer_notes;
			}
			setConflict(null);
			try {
				const apiTask = await updateTask(
					task.project_id,
					task.id,
					body,
				);
				const updated = toUiTask(apiTask);
				mutateTasks();
				setViewTask((vt) =>
					vt && vt.id === updated.id ? updated : vt,
				);
				setEditTask((et) =>
					et && et.id === updated.id ? updated : et,
				);
				toast.success("Task updated", { description: apiTask.title });
			} catch (e) {
				if (isTaskConflict(e)) {
					// Someone saved again between viewing and resolving.
					setConflict({ task, payload, server: e.data });
					return;
				}
				toast.error("Failed to update task", {
					description: (e as Error).message || "Please try again.",
				});
			}
		},
		[conflict, mutateTasks],
	);

	const cancelConflict = useCallback(() => {
		setConflict(null);
		setViewTask(null);
		setEditTask(null);
		mutateTasks();
	}, [mutateTasks]);

	// ── Loading / Error ───────────────────────────────────────────────────────

	if (loading) {
		return <TasksPageSkeleton />;
	}

	if (error) {
		return (
			<div className="flex flex-col items-center justify-center py-24 text-center">
				<p className="text-sm font-medium text-foreground mb-1">
					Failed to load tasks
				</p>
				<p className="text-xs text-muted">{error.message}</p>
			</div>
		);
	}

	// ── Render ───────────────────────────────────────────────────────────────

	return (
		<div className={cn("mx-auto px-4 py-8 sm:px-6")}>
			{/* Header */}
			<div className="flex flex-col gap-4 mb-8 sm:flex-row sm:items-center sm:justify-between max-w-[1280px] mx-auto">
				<div>
					<h1 className="text-3xl font-semibold text-foreground tracking-tight">
						Tasks
					</h1>
					<p className="text-sm text-muted mt-1">
						{totalTasks} tasks
						{filterProject === "all" && " across all projects"}
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<StartSprintButton
						nextSprint={nextPlannedSprint}
						onStarted={handleSprintStarted}
					/>
					<EndSprintButton
						activeSprint={activeSprint}
						onEnded={handleSprintEnded}
					/>
					<PermissionGate feature="Create & edit tasks">
						<Button
							className="flex items-center gap-2"
							onClick={() => setDialogOpen(true)}
						>
							<Plus className="h-4 w-4" />
							New Task
						</Button>
					</PermissionGate>
				</div>
			</div>

			<TaskFilters
				className={cn(
					" duration-500 transition-all mx-auto",
					expanded ? "max-w-full" : "max-w-[1280px]",
				)}
				projects={projects}
				profiles={profiles}
				sprints={filterSprintOptions}
				sprintsLoading={filterSprintsLoading}
				view={view}
				expanded={expanded}
				grouped={grouped}
				stages={stages}
				filterProject={filterProject}
				filterSprint={filterSprint}
				filterUserIds={filterUserIds}
				filterStatus={filterStatus}
				search={search}
				isFiltered={isFiltered}
				onFilterProjectChange={setFilterProject}
				onFilterSprintChange={setFilterSprint}
				onFilterUserIdsChange={setFilterUserIds}
				onFilterStatusChange={setFilterStatus}
				onSearchChange={setSearch}
				onViewChange={setView}
				onToggleExpand={() => setExpanded((v) => !v)}
				onToggleGroup={() => setGrouped((v) => !v)}
				onClearFilters={handleClearFilters}
			/>
			{/* Board view */}
			{view === "board" && (
				<DndContext
					sensors={sensors}
					collisionDetection={collisionDetection}
					onDragStart={onDragStart}
					onDragEnd={onDragEnd}
				>
					<div className="flex justify-between gap-2 sticky top-24 z-10 w-full -mb-8 pointer-events-none">
						<button
							onClick={() => scrollBoard(-1)}
							disabled={!canLeft}
							title="Scroll left"
							className="p-1 rounded-sm border border-border bg-surface text-muted hover:text-foreground hover:bg-muted-subtle transition-colors disabled:opacity-40 disabled:pointer-events-none shadow-sm pointer-events-auto"
						>
							<ChevronLeft className="h-4 w-4" />
						</button>
						<button
							onClick={() => scrollBoard(1)}
							disabled={!canRight}
							title="Scroll right"
							className="p-1 rounded-sm border border-border bg-surface text-muted hover:text-foreground hover:bg-muted-subtle transition-colors disabled:opacity-40 disabled:pointer-events-none shadow-sm pointer-events-auto"
						>
							<ChevronRight className="h-4 w-4" />
						</button>
					</div>
					<div
						className={cn(
							"duration-500 transition-all mx-auto",
							expanded ? "max-w-full" : "max-w-[1280px]",
						)}
					>
						{grouped ? (
							<SwimlaneBoard
								boardRef={boardRef}
								stages={boardStages}
								stageKeys={boardStageKeys}
								filteredColumns={filteredColumns}
								projectsById={projectsById}
								collapsedProjects={collapsedProjects}
								onToggleProject={toggleProject}
								collapsedStages={collapsedStages}
								onToggleStage={toggleStage}
								onDelete={handleDeleteTask}
								onView={setViewTask}
								onAdd={onAddTask}
							/>
						) : (
							<ColumnBoard
								boardRef={boardRef}
								stages={boardStages}
								filteredColumns={filteredColumns}
								projectsById={projectsById}
								onDelete={handleDeleteTask}
								onView={setViewTask}
								onAdd={onAddTask}
								collapsedStages={collapsedStages}
								onToggleStage={toggleStage}
							/>
						)}
					</div>

					<DragOverlay
						dropAnimation={{ duration: 150, easing: "ease" }}
					>
						{activeTask ? (
							<div className="w-[272px]">
								<TaskCardContent
									task={activeTask}
									projectsById={projectsById}
									isDragging
								/>
							</div>
						) : null}
					</DragOverlay>
				</DndContext>
			)}

			{/* List view */}
			{view === "list" && (
				<TaskTable
					tasks={flatFilteredTasks}
					projects={projects}
					onView={setViewTask}
				/>
			)}

			{/* Calendar view */}
			{view === "calendar" && (
				<div
					className={cn(
						"duration-500 transition-all mx-auto",
						expanded ? "max-w-full" : "max-w-[1280px]",
					)}
				>
					<Suspense fallback={<CalendarSkeleton />}>
						<CalendarView
							tasks={flatFilteredTasks}
							isValidating={tasksValidating}
							isFiltered={isFiltered}
							onRefresh={mutateTasks}
							onClearFilters={handleClearFilters}
							onView={setViewTask}
						/>
					</Suspense>
				</div>
			)}

			{hasMore && (
				<LoadMoreTasks
					loaded={rawTasks.length}
					total={totalTasks}
					loading={isLoadingMore}
					onLoadMore={loadMore}
				/>
			)}

			<NewTaskDrawerV2
				open={dialogOpen}
				onClose={() => {
					setDialogOpen(false);
					setChildParent(null);
					setNewTaskPreset(null);
				}}
				onCreate={handleCreateTask}
				projects={projects}
				profiles={profiles}
				parentTaskId={childParent?.id}
				parentTitle={childParent?.title}
				lockedProjectId={childParent?.project_id}
				stages={stages}
				filterProject={filterProject}
				initialStatus={newTaskPreset?.status}
				initialProjectId={newTaskPreset?.projectId}
			/>

			<EditTaskDialog
				task={editTask}
				onClose={() => setEditTask(null)}
				onSave={handleEditTask}
				projects={projects}
				profiles={profiles}
			/>

			{conflict && (
				<TaskConflictDialog
					open
					base={conflict.task}
					payload={conflict.payload}
					theirs={conflict.server}
					projects={projects}
					profiles={profiles}
					sprints={filterSprintOptions}
					stages={stages}
					submitting={false}
					onMerge={(text) => resolveConflict(true, text)}
					onOverride={() => resolveConflict(false)}
					onCancel={cancelConflict}
				/>
			)}

			<TaskDetailDrawerV2
				task={viewTask}
				projects={projects}
				profiles={profiles}
				stages={stages}
				escEnabled={!dialogOpen}
				onClose={() => {
					removeParams();
					setViewTask(null);
					mutateTasks();
				}}
				onSaveNotes={handleSaveNotes}
				onUpdate={handleEditTask}
				onChangeStatus={handleChangeStatus}
				onOpenTask={(t) => setViewTask(t)}
				onOpenParent={async (parentId) => {
					const local = allTasks.find((t) => t.id === parentId);
					if (local) {
						setViewTask(local);
						return;
					}
					if (!viewTask) return;
					try {
						const fetched = await getTaskDetails(
							viewTask.project_id,
							parentId,
						);
						setViewTask(toUiTask(fetched));
					} catch (e) {
						toast.error("Failed to open parent task", {
							description: (e as Error).message,
						});
					}
				}}
				onAddChild={(parent) => {
					setChildParent(parent);
					setDialogOpen(true);
				}}
				onEdit={(t) => {
					setViewTask(null);
					setEditTask(t);
				}}
				onDelete={(t) => {
					handleDeleteTask(t);
					setViewTask(null);
				}}
			/>
		</div>
	);
}
